import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth.jsx';
import { useTheme } from '../../hooks/useTheme.jsx';
import { useModalA11y } from '../../hooks/useModalA11y.js';
import { MOBILE_MORE_ITEMS } from './navItems.js';

/**
 * Drawer "Mais" da barra mobile (P23E): reúne os destinos que não cabem nas
 * 4 posições da barra (Trilhas, Caderno, Progresso) e as ações de conta
 * (Perfil/Plano, Configurações, Tema, Sair) — evita empilhar sete itens na
 * barra inferior.
 */
export const MobileMoreSheet = ({ open, onClose, onOpenSettings }) => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const modalA11y = useModalA11y(open, onClose, 'more-sheet-title');

  if (!open) return null;

  return (
    <div {...modalA11y} className="md:hidden fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="absolute bottom-0 inset-x-0 bg-surface-container-low rounded-t-2xl border-t border-outline-variant/10 pb-6 pt-3 max-h-[80vh] overflow-y-auto">
        <h2 id="more-sheet-title" className="sr-only">Mais opções</h2>
        <div className="w-10 h-1 bg-outline-variant/30 rounded-full mx-auto mb-3" />

        <nav className="flex flex-col px-3 space-y-0.5" aria-label="Mais destinos">
          {MOBILE_MORE_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={onClose}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                isActive ? 'text-primary bg-primary/10 font-medium' : 'text-on-surface/80 hover:bg-primary/5'
              }`}
            >
              <span className="material-symbols-outlined text-[18px]" style={{ fontVariationSettings: "'FILL' 1" }}>{item.icon}</span>
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="my-2 border-t border-outline-variant/10 mx-3" />

        <div className="flex flex-col px-3 space-y-0.5">
          <NavLink
            to="/billing"
            onClick={onClose}
            className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
              isActive ? 'text-primary bg-primary/10 font-medium' : 'text-on-surface/80 hover:bg-primary/5'
            }`}
          >
            <span className="material-symbols-outlined text-[18px]">workspace_premium</span>
            Perfil/Plano
          </NavLink>
          <button
            onClick={() => { onClose(); onOpenSettings?.(); }}
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-on-surface/80 hover:bg-primary/5 transition-colors"
          >
            <span className="material-symbols-outlined text-[18px]">settings</span>
            Configurações
          </button>
          <button
            onClick={toggleTheme}
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-on-surface/80 hover:bg-primary/5 transition-colors"
          >
            <span className="material-symbols-outlined text-[18px]">{theme === 'dark' ? 'light_mode' : 'dark_mode'}</span>
            {theme === 'dark' ? 'Modo claro' : 'Modo escuro'}
          </button>
          {user?.role === 'admin' && (
            <NavLink
              to="/admin"
              onClick={onClose}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                isActive ? 'text-primary bg-primary/10 font-medium' : 'text-on-surface/80 hover:bg-primary/5'
              }`}
            >
              <span className="material-symbols-outlined text-[18px]">admin_panel_settings</span>
              Painel Admin
            </NavLink>
          )}
          <button
            onClick={async () => { onClose(); await logout(); navigate('/'); }}
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-on-surface/80 hover:text-error transition-colors"
          >
            <span className="material-symbols-outlined text-[18px]">logout</span>
            Sair
          </button>
        </div>
      </div>
    </div>
  );
};
